import React, { useCallback, useEffect, useRef, useState } from "react";

import Box from "components/common/Box";

import { triggerWelcomeSlideHaptic } from "library/haptics/welcomeHaptics";

import WelcomeSlideContent from "./components/WelcomeSlideContent";
import WelcomeSlideFooter from "./components/WelcomeSlideFooter";
import { useWelcomeScreenStyles } from "./welcomeScreenStyles";
import { WELCOME_SLIDES } from "./welcomeSlides.config";
import { resolveSlideLayout } from "./welcomeSlideLayout";
import { useWelcomeSwipe } from "./welcomeSwipe";

const WelcomeScreen = ({ onComplete, onSlideView }) => {
  const classes = useWelcomeScreenStyles();
  const trackViewportRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const goToSlide = useCallback((index) => {
    const nextIndex = Math.max(0, Math.min(index, WELCOME_SLIDES.length - 1));

    setActiveIndex((prevIndex) => {
      if (prevIndex !== nextIndex) {
        triggerWelcomeSlideHaptic();
      }

      return nextIndex;
    });
  }, []);

  const {
    setActiveIndexRef,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    trackStyle,
    isDragging,
    slideWidth,
  } = useWelcomeSwipe({
    slides: WELCOME_SLIDES,
    activeIndex,
    goToSlide,
    containerRef: trackViewportRef,
  });

  useEffect(() => {
    setActiveIndexRef(activeIndex);
  }, [activeIndex, setActiveIndexRef]);

  useEffect(() => {
    if (typeof onSlideView === "function") {
      onSlideView(activeIndex);
    }
  }, [activeIndex, onSlideView]);

  const slidePaneWidth = slideWidth || undefined;
  const slidePaneWidthPercent = `${100 / WELCOME_SLIDES.length}%`;

  return (
    <Box className={classes.root}>
      <Box
        ref={trackViewportRef}
        className={classes.trackViewport}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
      >
        <Box
          className={classes.track}
          style={{
            ...trackStyle,
            width: slidePaneWidth
              ? slidePaneWidth * WELCOME_SLIDES.length
              : `${WELCOME_SLIDES.length * 100}%`,
          }}
        >
          {WELCOME_SLIDES.map((item, index) => {
            const layout = resolveSlideLayout(item);
            const isActive = index === activeIndex;

            return (
              <Box
                key={item.id}
                className={classes.slidePane}
                style={{
                  width: slidePaneWidth || slidePaneWidthPercent,
                  flexBasis: slidePaneWidth || slidePaneWidthPercent,
                }}
                sx={{ backgroundColor: item.background }}
              >
                <Box
                  className={classes.slideBody}
                  sx={{
                    overflowY: layout.bodyScrollable ? "auto" : "hidden",
                  }}
                >
                  <Box
                    className={`${classes.slideContentWrapper} ${
                      isActive && !isDragging ? classes.slideContentEnter : ""
                    }`}
                    key={isActive ? `slide-${activeIndex}` : item.id}
                  >
                    <WelcomeSlideContent
                      slide={item}
                      classes={classes}
                      onComplete={onComplete}
                    />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <WelcomeSlideFooter
        slides={WELCOME_SLIDES}
        activeIndex={activeIndex}
        onGoToSlide={goToSlide}
        classes={classes}
      />
    </Box>
  );
};

export default React.memo(WelcomeScreen);
